/**
 * Business card form and preview.
 *
 * Lifted out of AdminBusinessCardsPage, which the profile page was importing a
 * component *from* — the form and its state shape existed in two files and had
 * to be kept in step by hand. The form's state shape and its defaults live in
 * `utils/businessCardForm.ts`; see the note there for why they are separate.
 */
import type { BusinessCardTemplate } from '../types'
import type { CardFormState } from '../utils/businessCardForm'
import { TrashIcon } from './Icons'

interface CardFormProps {
  form: CardFormState
  setForm: React.Dispatch<React.SetStateAction<CardFormState>>
  onSubmit: (e: React.FormEvent) => void
  saving: boolean
  error: string
  submitLabel: string
}

export function CardForm({ form, setForm, onSubmit, saving, error, submitLabel }: CardFormProps) {
  function field(key: keyof CardFormState) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [key]: e.target.value }))
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="label">Card Name (internal label) *</label>
          <input
            type="text"
            className="input"
            required
            maxLength={100}
            placeholder="e.g. Company — Main"
            value={form.name}
            onChange={field('name')}
          />
        </div>
        <div>
          <label className="label">Sender Name *</label>
          <input
            type="text"
            className="input"
            required
            maxLength={100}
            placeholder="Full name shown on card"
            value={form.sender_name}
            onChange={field('sender_name')}
          />
        </div>
        <div>
          <label className="label">Title / Role</label>
          <input
            type="text"
            className="input"
            maxLength={100}
            placeholder="e.g. FCC License Specialist"
            value={form.title}
            onChange={field('title')}
          />
        </div>
        <div>
          <label className="label">Email on Card</label>
          <input
            type="email"
            className="input"
            maxLength={255}
            placeholder="contact@example.com"
            value={form.email}
            onChange={field('email')}
          />
        </div>
        <div>
          <label className="label">Phone on Card</label>
          <input
            type="tel"
            className="input"
            maxLength={30}
            placeholder="(555) 555-5555"
            value={form.phone}
            onChange={field('phone')}
          />
        </div>
        <div className="sm:col-span-2">
          <label className="label">Greeting / Intro Text</label>
          <textarea
            className="input min-h-[80px]"
            maxLength={2000}
            placeholder="Optional opening paragraph placed above the business card in the email…"
            value={form.greeting_text}
            onChange={field('greeting_text')}
          />
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="flex justify-end gap-3">
        <button type="submit" disabled={saving} className="btn btn-primary">
          {saving ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  )
}

interface BusinessCardPreviewProps {
  card: BusinessCardTemplate
  onEdit: () => void
  onDelete: () => void
  onToggleActive: () => void
  /** Send this card to a typed address. Omitted for a card that cannot be sent. */
  onSend?: () => void
}

export function BusinessCardPreview({
  card,
  onEdit,
  onDelete,
  onToggleActive,
  onSend,
}: BusinessCardPreviewProps) {
  return (
    <div className={`card flex flex-col gap-3 ${!card.is_active ? 'opacity-50' : ''}`}>
      {/* Card visual preview */}
      <div className="flex gap-0 rounded border border-gray-200 overflow-hidden">
        <div className="w-1.5 bg-blue-900 flex-shrink-0" />
        <div className="p-3 flex-1 min-w-0">
          <div className="font-bold text-blue-900 text-sm leading-tight truncate">{card.sender_name}</div>
          {card.title && <div className="text-xs text-gray-500 italic truncate">{card.title}</div>}
          <div className="mt-2 space-y-0.5">
            {card.email && (
              <div className="text-xs text-gray-600 truncate">
                <span className="font-semibold text-blue-900 uppercase text-[10px] tracking-wide mr-1">Email</span>
                {card.email}
              </div>
            )}
            {card.phone && (
              <div className="text-xs text-gray-600">
                <span className="font-semibold text-blue-900 uppercase text-[10px] tracking-wide mr-1">Phone</span>
                {card.phone}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Meta row */}
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span className="font-medium text-gray-700 truncate mr-2">{card.name}</span>
        <span
          className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${card.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
        >
          {card.is_active ? 'Active' : 'Inactive'}
        </span>
      </div>

      {/* Actions. Send is primary — looking at your cards, the thing you most
          often want is to send one. It is hidden on an inactive card because the
          API refuses to send those. */}
      <div className="flex gap-2 flex-wrap">
        {onSend && card.is_active && (
          <button className="btn btn-primary btn-sm flex-1" onClick={onSend}>
            Send
          </button>
        )}
        <button className="btn btn-secondary btn-sm flex-1" onClick={onEdit}>Edit</button>
        <button
          className="btn btn-secondary btn-sm flex-1"
          onClick={onToggleActive}
        >
          {card.is_active ? 'Deactivate' : 'Activate'}
        </button>
        <button
          className="btn btn-secondary btn-sm px-2 text-gray-400 hover:text-red-600 hover:border-red-200"
          onClick={onDelete}
          aria-label="Delete card"
          title="Delete card"
        >
          <TrashIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}
